import React, { useRef } from 'react';
import { InvestorResource } from '../../common/entities';
import InvestorSearchResult from './InvestorSearchResult';
import { Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

/**
 * @description Properties to be passed to this component.
 * @interface
 */
interface Props {
  investors: InvestorResource[];
  busy?: boolean;
  searchQuery?: string | null;
  filters?: string | null;
  page?: string | null;
  totalPages?: number;
}

/**
 * @description Investor List component, high level information about investors services.
 * @param {Props} props
 * @return {JSX.Element}
 */
const SearchResults = (props: Props): JSX.Element => {
  const page = props.page ? parseInt(props.page) : 1;
  const nextPage = page + 1;
  const prevPage = page > 1 ? page - 1 : 1;

  const topRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () =>
    topRef.current &&
    topRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (props.busy) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  const paginationButtons = (
    <div className="d-flex justify-content-between mb-3">
      <Link
        className="btn btn-sm btn-outline-dark"
        to={{
          search: `?q=${props.searchQuery}&a=${props.filters}&p=${prevPage}`,
        }}
      >
        Previous
      </Link>
      <Link
        className="btn btn-sm btn-outline-dark"
        to={{
          search: `?q=${props.searchQuery}&a=${props.filters}&p=${nextPage}`,
        }}
      >
        Next
      </Link>
    </div>
  );

  scrollToTop();

  return (
    <React.Fragment>
      <p ref={topRef}>
        <small>
          Page {page} of {props.totalPages}
        </small>
      </p>
      {props.investors.length > 0 ? (
        <React.Fragment>
          {paginationButtons}
          <hr />
          {props.investors.map((investor: InvestorResource) => (
            <InvestorSearchResult key={investor.id} investor={investor} />
          ))}
          {paginationButtons}
        </React.Fragment>
      ) : (
        <Row>
          <Col xs={12}>
            <p>There are no investors that match your query.</p>
            <Link
              className="btn btn-sm btn-outline-dark"
              to={{ search: `?q=${props.searchQuery}&a=` }}
            >
              Clear
            </Link>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

export default SearchResults;
